import { memo } from "react";
import type { TokenId } from "@/model/tokens";
import { cn } from "@/ui/kit/cn";
import { A, C } from "./tok";
import { v } from "./vars";

function Prompt({ cmd }: { cmd?: string }) {
  return (
    <div className="whitespace-pre">
      <A i={6} bold>
        ~/code/themes
      </A>{" "}
      on{" "}
      <A i={5} bold>
        ⎇ main
      </A>{" "}
      <A i={1}>[!?]</A> via <A i={2}>⬢ v24.21</A>
      <br />
      <A i={2} bold>
        ❯
      </A>{" "}
      {cmd}
    </div>
  );
}

const ANSI_LABELS = ["blk", "red", "grn", "yel", "blu", "mag", "cyn", "wht"];

export function AnsiGrid() {
  return (
    <div className="grid grid-cols-8 gap-1 font-sans">
      {Array.from({ length: 16 }, (_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-md ring-1 ring-black/20"
          data-token={`terminal.ansi${i}`}
        >
          <div className="h-7" style={{ background: v(`terminal.ansi${i}` as TokenId) }} />
          <div
            className="px-1.5 py-0.5 text-[10px]"
            style={{ background: v("terminal.background"), color: v("terminal.foreground") }}
          >
            {i} {ANSI_LABELS[i % 8]}
          </div>
        </div>
      ))}
    </div>
  );
}

export const TerminalBody = memo(function TerminalBody({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={cn(
        "space-y-[0.6lh] p-3",
        compact ? "text-[11px] leading-[1.45]" : "text-[13px] leading-[1.55]",
      )}
    >
      <div>
        <Prompt cmd="ls --color" />
        <div className="whitespace-pre">
          <A i={4} bold>
            assets/
          </A>{" "}
          <A i={4} bold>
            dist/
          </A>{" "}
          <A i={4} bold>
            src/
          </A>{" "}
          <A i={2} bold>
            build.sh*
          </A>{" "}
          README.md{" "}
          <A i={1} bold>
            themes.tar.gz
          </A>{" "}
          <A i={6} bold>
            docs
          </A>{" "}
          -&gt;{" "}
          <A i={4} bold>
            site/
          </A>
        </div>
      </div>
      <div>
        <Prompt cmd="git log --oneline --graph -4" />
        <div className="whitespace-pre">
          <A i={1}>*</A> <A i={3}>74ee316</A> <A i={3}>(</A>
          <A i={6} bold>
            HEAD -&gt;{" "}
          </A>
          <A i={2} bold>
            main
          </A>
          <A i={3}>, </A>
          <A i={1} bold>
            origin/main
          </A>
          <A i={3}>)</A> feat: add theme model
          <br />
          <A i={1}>*</A> <A i={3}>1a2b3c4</A> fix(neovim): escape lua keywords
          <br />
          <A i={1}>|</A> <A i={2}>*</A> <A i={3}>9f8e7d6</A> <A i={3}>(</A>
          <A i={2} bold>
            previews
          </A>
          <A i={3}>)</A> wip: previews
          <br />
          <A i={1}>|</A>
          <A i={2}>/</A>
        </div>
      </div>
      <div>
        <Prompt cmd="git diff --color" />
        <div className="whitespace-pre">
          <A i={15} bold>
            diff --git a/src/theme.ts b/src/theme.ts
          </A>
          <br />
          <A i={6}>@@ -12,3 +12,3 @@</A> export const theme = {"{"}
          <br />
          <A i={1}>- accent: "$blue",</A>
          <br />
          <A i={2}>+ accent: "$purple",</A>
        </div>
      </div>
      {!compact && (
        <div>
          <Prompt cmd="make check" />
          <div className="whitespace-pre">
            <A i={8}>[12:04:51]</A>{" "}
            <A i={2} bold>
              ✓
            </A>{" "}
            lint{" "}
            <A i={2} bold>
              ✓
            </A>{" "}
            types{" "}
            <A i={3} bold>
              !
            </A>{" "}
            2 warnings{" "}
            <A i={1} bold>
              ✗
            </A>{" "}
            e2e <A i={8}>(skipped: no browser)</A>
            <br />
            <span
              data-token="terminal.selectionBg"
              style={{ background: v("terminal.selectionBg"), color: v("terminal.selectionFg") }}
            >
              selected text uses selection-background
            </span>
            <br />
            <A i={9}>bright red</A> <A i={10}>bright green</A> <A i={11}>bright yellow</A>{" "}
            <A i={12}>bright blue</A> <A i={13}>bright magenta</A> <A i={14}>bright cyan</A>
          </div>
        </div>
      )}
      <div className="whitespace-pre">
        <A i={2} bold>
          ❯
        </A>{" "}
        <C
          token="terminal.cursor"
          style={{ background: v("terminal.cursor"), color: v("terminal.cursorText") }}
        >
          {" "}
        </C>
        <A i={8}> type a command…</A>
      </div>
    </div>
  );
});
